/**
 * An input layer with no device behind it.
 *
 * `createInput` exists to turn a keyboard and a gamepad into the game's action
 * set. This turns *anything else* into the same action set — a fighter AI, an
 * attract-mode demo, a replay — and hands back an object `createPlayerControl`
 * cannot tell from the real one.
 *
 * That indistinguishability is the whole point. A driver that calls
 * `throwStrike` directly is a driver that skips whatever the control layer
 * decides on the way: the binding, the combo resolution, the order strikes and
 * movement are read in, the escape-from-a-hold branch. Every one of those is a
 * rule, and a second caller that re-implements them is a second caller that
 * will drift from the first. So the AI does not get a private door into the
 * fighter. It gets a controller.
 *
 * The edge rules are copied from `input.js` deliberately, right down to being
 * computed against the previous frame's held set: a verb that stays in the
 * action set two frames running is *held*, and `pressed` is true on the first
 * of them only. An AI that leans on `light` therefore machine-guns nothing,
 * exactly as a player leaning on the key would not.
 */

import { ACTIONS } from './inputMap.js';

export class VirtualInputError extends Error {}

const ZERO = Object.freeze({ x: 0, y: 0 });

/** A stick cannot be pushed past its own edge, so neither can this. */
function clampVector(move) {
  const x = Number(move?.x) || 0;
  const y = Number(move?.y) || 0;
  const length = Math.hypot(x, y);
  if (length <= 1) return { x, y };
  return { x: x / length, y: y / length };
}

export function createVirtualInput() {
  let move = ZERO;
  let held = new Set();
  let pressed = new Set();
  let released = new Set();

  return {
    /** `{ x, y }`, +y forward and +x to the fighter's right. Never longer than 1. */
    get move() {
      return move;
    },
    /** Verbs currently held, in the canonical order. */
    get actions() {
      return ACTIONS.filter((name) => held.has(name));
    },
    /** There is no pad. Answered so this is drop-in where an input is read. */
    get gamepad() {
      return null;
    },
    get hasGamepad() {
      return false;
    },

    held: (action) => held.has(action),
    pressed: (action) => pressed.has(action),
    released: (action) => released.has(action),

    /**
     * One frame of intent, with the edges recomputed against the last one.
     *
     * An action nobody has heard of throws rather than doing nothing quietly:
     * a driver asking for `punch` instead of `light` would otherwise stand
     * there for the whole round with no way to tell why.
     */
    push({ move: next = ZERO, actions = [] } = {}) {
      for (const name of actions) {
        if (!ACTIONS.includes(name)) {
          throw new VirtualInputError(`"${name}" is not one of ${ACTIONS.join(', ')}`);
        }
      }
      const previous = held;
      held = new Set(actions);
      pressed = new Set([...held].filter((name) => !previous.has(name)));
      released = new Set([...previous].filter((name) => !held.has(name)));
      move = clampVector(next);
      return held;
    },

    /**
     * Nothing to poll — `push` already fixed this frame's edges. Present so a
     * consumer that calls `input.update()` once a frame keeps working when a
     * virtual input is swapped in for a real one.
     */
    update() {},

    clear() {
      held = new Set();
      pressed = new Set();
      released = new Set();
      move = ZERO;
    },

    dispose() {},
  };
}
